import { useState, useEffect, useRef } from 'react';
import { C } from '../../../lib/tokens.js';
import { t } from '../../../lib/data.js';
import { useLookups } from '../../../lib/LookupsContext.jsx';
import { ChatIntake } from './ChatIntake.jsx';
import { DRAFT_DEFAULTS } from './shared.jsx';

// ─── Main Controller Component ────────────────────────────────────────────────
export function CreateRequestPage({ onCancel, onSubmit, onFindSimilar, onReply, onRegisterGuard, role, me }) {
  const { priorities } = useLookups();
  const [draft, setDraft] = useState(DRAFT_DEFAULTS);
  const [submitting, setSubmitting] = useState(false);

  // Guards against spam-clicking Create: ignore re-entrant submits while one is
  // already in flight, and re-enable the button if it fails so the user can retry.
  async function handleSubmit(d) {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onSubmit?.(d);
    } catch {
      setSubmitting(false);
    }
  }
  const update = (k, v) => setDraft(d => (
    k === 'bpartnerId' && v !== d.bpartnerId ? { ...d, bpartnerId: v, contactId: '' } : { ...d, [k]: v }
  ));

  useEffect(() => {
    if (priorities.length > 0 && !draft.priority) {
      const normal = priorities.find(p => p.id === 'normal') ?? priorities[Math.floor(priorities.length / 2)];
      setDraft(d => d.priority ? d : { ...d, priority: normal.id });
    }
  }, [priorities, draft.priority]);

  const isDirtyRef = useRef(false);
  isDirtyRef.current = !!(draft.type || draft.summary || draft.description || draft.attachments.length);

  useEffect(() => {
    const guard = () => {
      if (!isDirtyRef.current) return true;
      return window.confirm('You have unsaved changes. Leave and discard?');
    };
    onRegisterGuard?.(guard);
    return () => onRegisterGuard?.(null);
  }, [onRegisterGuard]);

  // The guard above only catches in-app navigation (React Router) - closing the
  // tab or hitting refresh bypasses it entirely, since that's not a route change.
  // beforeunload is the only hook the browser gives for that; it can't show a
  // custom message (browsers ignore it for security), just its own native prompt.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const handleCancel = () => {
    if (isDirtyRef.current && !window.confirm('You have unsaved changes. Leave and discard?')) return;
    onCancel?.();
  };

  return (
    <div style={{ flex: 1, overflow: 'auto', background: C.platinum }}>
      <div style={{ background: C.tin, borderBottom: `1px solid ${C.mercury}`, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontFamily: 'Roboto', fontWeight: 300, fontSize: 26, color: C.deepsea }}>{t('create.title')}</h1>
          </div>
        </div>
      </div>
      <ChatIntake onCancel={handleCancel} onSubmit={handleSubmit} submitting={submitting} draft={draft} update={update} role={role} me={me}
        onFindSimilar={onFindSimilar} onReply={onReply} />
    </div>
  );
}
